import { useState, useCallback, useMemo, useEffect } from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { DrawerSessionPair } from './useDrawerAskAI'

interface UseFloatingAskAISessionArgs {
  submitMessage: (message: string, options?: { messageType?: string }) => Promise<void>
  messages: Array<MessageItem>
  isStreaming: boolean
  isActive: boolean
}

interface UseFloatingAskAISessionReturn {
  handleSubmit: (message: string) => Promise<void>
  latestResponse: MessageItem | undefined
  sessionHistory: Array<DrawerSessionPair>
  isResponseStreaming: boolean
  isWaitingForResponse: boolean
  hasActiveSession: boolean
}

const NO_SESSION = {
  latestResponse: undefined,
  sessionHistory: [] as Array<DrawerSessionPair>,
  isResponseStreaming: false,
  isWaitingForResponse: false,
}

interface FloatingAskAISessionState {
  /** `isActive` when the session was last (re)started, so a change can restart it during render. */
  isActive: boolean
  /** Index into `messages` where the current session begins. */
  startIndex: number
  /** Whether the user has asked something from the floating composer during this session. */
  hasAsked: boolean
}

export function useFloatingAskAISession({
  submitMessage,
  messages,
  isStreaming,
  isActive,
}: UseFloatingAskAISessionArgs): UseFloatingAskAISessionReturn {
  // Only messages added after the composer became active belong to its session, so the
  // index is captured on that transition and then left alone for the rest of the session.
  const [session, setSession] = useState<FloatingAskAISessionState>(() => ({
    isActive,
    startIndex: messages.length,
    hasAsked: false,
  }))

  // Re-synced during render rather than in an effect: an effect commits one render with the
  // stale index first, which paints a frame of whatever turn another surface just sent.
  if (session.isActive !== isActive) {
    setSession(isActive
      ? { isActive, startIndex: messages.length, hasAsked: false }
      : { ...session, isActive })
  }

  // A refresh mounts this hook with an empty message list, so anchoring at `messages.length`
  // once would make the whole restored conversation count as the current session and pop the
  // bubble open. Until the user actually asks, the anchor follows the tail; after that it is
  // frozen so the pair survives its own answer streaming in. Skipped while inactive, where
  // the anchor is deliberately left alone and every message would otherwise cost a re-render.
  useEffect(() => {
    if (!isActive) return

    setSession(previous => (
      previous.hasAsked || previous.startIndex === messages.length
        ? previous
        : { ...previous, startIndex: messages.length }
    ))
  }, [isActive, messages.length])

  const handleSubmit = useCallback(async(message: string) => {
    setSession(previous => (previous.hasAsked ? previous : { ...previous, hasAsked: true }))
    await submitMessage(message)
  }, [submitMessage])

  const { latestResponse, sessionHistory, isResponseStreaming, isWaitingForResponse } = useMemo(() => {
    // The composer stays mounted on every tab, so it re-renders on each streamed token.
    // Nothing is shown while it is inactive; skip the derivation rather than walk the
    // whole message list for a render that returns null.
    if (!isActive) return NO_SESSION

    const sessionMessages = messages.slice(session.startIndex)
    const pairs: Array<DrawerSessionPair> = []

    for (let i = 0; i < sessionMessages.length; i++) {
      const msg = sessionMessages[i]
      if (msg.role !== 'user') continue

      const nextMsg = sessionMessages[i + 1]
      const answer = nextMsg?.role === 'assistant' ? nextMsg : undefined
      pairs.push({ question: msg, answer })
    }

    const latestPair = pairs.length > 0 ? pairs[pairs.length - 1] : undefined
    const latestResponse = latestPair?.answer
    const lastMessage = messages[messages.length - 1]
    const isResponseStreaming = !!(isStreaming && lastMessage?.id === latestResponse?.id)
    const isWaitingForResponse = !!(
      isStreaming
      && latestPair?.answer === undefined
      && lastMessage?.id === latestPair?.question.id
    )

    return { latestResponse, sessionHistory: pairs, isResponseStreaming, isWaitingForResponse }
  }, [messages, session.startIndex, isStreaming, isActive])

  return {
    handleSubmit,
    latestResponse,
    sessionHistory,
    isResponseStreaming,
    isWaitingForResponse,
    hasActiveSession: session.hasAsked && sessionHistory.length > 0,
  }
}
